import React from "react";
import { Document, Page, Text, View, StyleSheet } from "@react-pdf/renderer";
import { format } from "date-fns";

const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontSize: 12,
    fontFamily: "Helvetica"
  },
  header: {
    marginBottom: 20,
    textAlign: "center"
  },
  title: {
    fontSize: 16,
    fontWeight: "bold",
    marginBottom: 10
  },
  table: {
    display: "table",
    width: "100%",
    borderStyle: "solid",
    borderWidth: 1,
    borderColor: "#bfbfbf"
  },
  // Row styles
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#bfbfbf",
    minHeight: 25
  },
  tableHeader: {
    backgroundColor: "#f0f0f0",
    fontWeight: "bold"
  },
  // Basic cell style
  tableCell: {
    borderRightWidth: 1,
    borderRightColor: "#bfbfbf",
    fontSize: 8,
    padding: 5,
    textAlign: "left"
  },
  lastCell: {
    borderRightWidth: 0
  },
  // Column widths - adjusted for better spacing
  sNo: { width: "5%" },
  indentId: { width: "10%" },
  itemName: { width: "24%" }, // Increased for remarks
  itemId: { width: "8%" },
  unit: { width: "6%" },
  quantity: { width: "9%" },
  qtyRecd: { width: "14%" }, // Increased for remarks
  project: { width: "8%" },
  status: { width: "16%" },
  
  // Cell content styles
  cellContent: {
    padding: 2
  },
  
  // Remarks styles - improved to prevent overlapping
  remarksContainer: {
    paddingTop: 4,
    paddingBottom: 2
  },
  remarksLabel: {
    fontSize: 7,
    fontWeight: "bold",
    color: "#444",
    marginRight: 2
  },
  remarksContent: {
    fontSize: 7,
    color: "#666",
    flexShrink: 1,
    flexWrap: "wrap"
  },
  // Indent remarks layout
  indentRemarksRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    paddingTop: 2,
    paddingBottom: 1
  },
  // Received remarks layout
  receivedRemarksRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    paddingTop: 2,
    paddingBottom: 1
  },
  // Debug info
  debugInfo: {
    fontSize: 6,
    color: "red",
    marginTop: 2
  }
});

export const IndentPDF = ({ data, orientation = "portrait" }) => {
  const currentDateTime = format(new Date(), "dd/MM/yyyy HH:mm:ss");
  
  // Helper function to check if a value is NaN
  const isNaN = (value) => {
    return value !== value; // This is the standard way to check for NaN
  };
  
  // Helper function to check if remarks have actual content
  const hasActualRemarks = (remarks) => {
    return remarks && remarks.trim() !== "" && remarks.trim() !== "N/A";
  };
  
  // Helper function to format status text
  const formatStatusText = (item) => {
    // Check if daysLeft is a valid number
    if (item.daysLeft !== undefined && item.daysLeft !== null && !isNaN(item.daysLeft)) {
      return item.daysLeft < 0 
        ? `${Math.abs(item.daysLeft)} days overdue` 
        : `${item.daysLeft} days left`;
    }
    
    // If dueDate is available, try to calculate days left
    if (item.dueDate && item.dueDate !== 'N/A') {
      try {
        const dueDate = new Date(item.dueDate);
        const today = new Date();
        const diffTime = dueDate - today;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        if (!isNaN(diffDays)) {
          return diffDays < 0 
            ? `${Math.abs(diffDays)} days overdue` 
            : `${diffDays} days left`;
        }
      } catch (err) {
        console.error('Error calculating days from dueDate:', err);
      }
    }
    
    // Fallback to status if available
    if (item.status && item.status !== 'NaN days left') {
      return item.status;
    }
    
    // Default fallback
    return 'Due date not available';
  };

  return (
    <Document>
      <Page size="A4" orientation={orientation} style={styles.page}>
        <View style={styles.header}>
          <Text style={styles.title}>Alerts Report</Text>
          <Text style={{ fontSize: 10 }}>Generated on: {currentDateTime}</Text>
        </View>

        <View style={styles.table}>
          {/* Table Header */}
          <View style={[styles.tableRow, styles.tableHeader]}>
            <Text style={[styles.tableCell, styles.sNo]}>S.No</Text>
            <Text style={[styles.tableCell, styles.indentId]}>Indent ID</Text>
            <Text style={[styles.tableCell, styles.itemName]}>Item Name</Text>
            <Text style={[styles.tableCell, styles.itemId]}>Item ID</Text>
            <Text style={[styles.tableCell, styles.unit]}>Unit</Text>
            <Text style={[styles.tableCell, styles.quantity]}>Quantity</Text>
            <Text style={[styles.tableCell, styles.qtyRecd]}>Qty Rcvd</Text>
            <Text style={[styles.tableCell, styles.project]}>Project</Text>
            <Text style={[styles.tableCell, styles.status, styles.lastCell]}>Status</Text>
          </View>

          {/* Table Rows */}
          {data.map((item, index) => {
            // Calculate status text using the helper function
            const statusText = formatStatusText(item);
            
            return (
              <View key={index} wrap={false}>
                <View style={styles.tableRow}>
                  {/* S.No */}
                  <View style={[styles.tableCell, styles.sNo]}>
                    <Text style={styles.cellContent}>{index + 1}</Text>
                  </View>
                  
                  {/* Indent ID */}
                  <View style={[styles.tableCell, styles.indentId]}>
                    <Text style={styles.cellContent}>{item.indentId}</Text>
                  </View>
                  
                  {/* Item Name with indent remarks */}
                  <View style={[styles.tableCell, styles.itemName]}>
                    <Text style={styles.cellContent}>{item.itemName}</Text>
                    {hasActualRemarks(item.description) && (
                      <View style={styles.remarksContainer}>
                        <View style={styles.indentRemarksRow}>
                          <Text style={styles.remarksLabel}>Indent Remarks:</Text>
                        </View>
                        <View style={styles.indentRemarksRow}>
                          <Text style={styles.remarksContent}>
                            {item.description}
                          </Text>
                        </View>
                      </View>
                    )}
                  </View>
                  
                  {/* Item ID */}
                  <View style={[styles.tableCell, styles.itemId]}>
                    <Text style={styles.cellContent}>{item.itemId}</Text>
                  </View>
                  
                  {/* Unit */}
                  <View style={[styles.tableCell, styles.unit]}>
                    <Text style={styles.cellContent}>{item.unit}</Text>
                  </View>
                  
                  {/* Quantity */}
                  <View style={[styles.tableCell, styles.quantity]}>
                    <Text style={styles.cellContent}>{item.quantity}</Text>
                  </View>
                  
                  {/* Qty Rcvd with received remarks */}
                  <View style={[styles.tableCell, styles.qtyRecd]}>
                    <Text style={styles.cellContent}>{item.qtyreceived}</Text>
                    {hasActualRemarks(item.remarks) && (
                      <View style={styles.remarksContainer}>
                        <View style={styles.receivedRemarksRow}>
                          <Text style={styles.remarksLabel}>Received Remarks:</Text>
                        </View>
                        <View style={styles.receivedRemarksRow}>
                          <Text style={styles.remarksContent}>
                            {item.remarks}
                          </Text>
                        </View>
                      </View>
                    )}
                  </View>
                  
                  {/* Project */}
                  <View style={[styles.tableCell, styles.project]}>
                    <Text style={styles.cellContent}>{item.projectName}</Text>
                  </View>
                  
                  {/* Status */}
                  <View style={[styles.tableCell, styles.status, styles.lastCell]}>
                    <Text style={styles.cellContent}>{statusText}</Text>
                    {/* Debug info - will show in PDF 
                    <Text style={styles.debugInfo}>
                      DL: {item.daysLeft !== undefined ? (isNaN(item.daysLeft) ? 'NaN' : item.daysLeft) : 'undef'} | 
                      S: {item.status || 'none'} |
                      DD: {item.dueDate || 'none'}
                    </Text>*/}
                  </View>
                </View>
            </View>
            );
          })}
        </View>
      </Page>
    </Document>
  );
};